import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { AUTHORITY_TYPE, GENDER, NIR_INHERITANCE_FROM } from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
} from "utils/helpers/input";
import {
  childNirIsManualEntry,
  childRequiresOffContractSpouseNir,
  getNirInheritanceFromStored,
  resolveChildInheritedNir,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/relatedPersonChild";
import ActionButtons from "../Components/ActionButtons";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import FaceFemale from "../Components/SVG/FaceFemale";
import FaceMale from "../Components/SVG/FaceMale";
import validation from "./validation";

const NUMBERS_MAPPING = {
  1: "1er",
  2: "2ème",
  3: "3ème",
  4: "4ème",
  5: "5ème",
  6: "6ème",
  7: "7ème",
  8: "8ème",
  9: "9ème",
  10: "10ème",
  11: "11ème",
  12: "12ème",
};

const isPendingParentNir = ({
  authority,
  nirInheritanceFrom,
  adherentNir,
  spouseNir,
  hasSpouse,
}) => {
  if (childNirIsManualEntry(authority, hasSpouse, nirInheritanceFrom)) {
    return false;
  }
  const adherent = (adherentNir || "").trim();
  const spouse = (spouseNir || "").trim();
  if (authority === AUTHORITY_TYPE.FATHER) return !adherent;
  if (authority === AUTHORITY_TYPE.MOTHER) return hasSpouse && !spouse;
  if (authority === AUTHORITY_TYPE.BOTH) {
    if (nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE)
      return !hasSpouse || !spouse;
    return !adherent;
  }
  return false;
};

function View({
  children,
  spouse,
  adherentSocialSecurityNumber = "",
  adherentSocialRegime = "",
  onNext,
  onBack,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const formTranslate = t("RELATED_PERSONS.FORM", { returnObjects: true });
  const sectionTranslate = t("RELATED_PERSONS.SECTION", {
    returnObjects: true,
  });
  const headerTranslate = t("RELATED_PERSONS.HEADER", { returnObjects: true });
  const { submitForm, values, setFieldValue, ...formProps } = useFormik({
    initialValues: { children, spouse },
    onSubmit: onNext,
    enableReinitialize: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        hasPartner: !isEmpty(spouse),
        adherentSocialRegime,
      },
    ),
  });

  const hasSpouse = !isEmpty(spouse);

  const spouseNirSynced = get(values, "spouse.identity.socialSecurityNumber");
  const childrenAuthorityKey = (values.children || [])
    .map((c) => `${c?.authority ?? ""}|${getNirInheritanceFromStored(c) ?? ""}`)
    .join(";");

  useEffect(() => {
    const spouseNir = spouseNirSynced;
    (values.children || []).forEach((child, i) => {
      if (child.authority == null) return;
      const nirInheritanceFrom = getNirInheritanceFromStored(child);
      if (
        childNirIsManualEntry(child.authority, hasSpouse, nirInheritanceFrom)
      ) {
        if (
          childRequiresOffContractSpouseNir(
            child.authority,
            hasSpouse,
            nirInheritanceFrom,
          )
        ) {
          const cur =
            get(values, `children.${i}.identity.socialSecurityNumber`) ?? "";
          const adherent = (adherentSocialSecurityNumber || "").trim();
          if (cur && cur === adherent) {
            setFieldValue(`children.${i}.identity.socialSecurityNumber`, "");
          }
        }
        return;
      }
      const next = resolveChildInheritedNir({
        authority: child.authority,
        nirInheritanceFrom,
        adherentNir: adherentSocialSecurityNumber,
        spouseNir,
        hasSpouse,
      });
      const cur =
        get(values, `children.${i}.identity.socialSecurityNumber`) ?? "";
      const normalizedNext = next ?? "";
      if (cur !== normalizedNext) {
        setFieldValue(
          `children.${i}.identity.socialSecurityNumber`,
          normalizedNext,
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit full `values` to avoid sync loops
  }, [
    adherentSocialSecurityNumber,
    spouseNirSynced,
    childrenAuthorityKey,
    hasSpouse,
    setFieldValue,
  ]);

  const pendingParentLabel = (authority, nirInheritanceFrom) => {
    if (authority === AUTHORITY_TYPE.FATHER) {
      return t("RELATED_PERSONS.PARENT_NAME_ADHERENT");
    }
    if (authority === AUTHORITY_TYPE.MOTHER) {
      return t("RELATED_PERSONS.PARENT_NAME_SPOUSE");
    }
    if (authority === AUTHORITY_TYPE.BOTH) {
      return nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE
        ? t("RELATED_PERSONS.PARENT_NAME_SPOUSE")
        : t("RELATED_PERSONS.PARENT_NAME_ADHERENT");
    }
    return "";
  };

  const civiliteOptions = [
    {
      value: GENDER.MALE,
      label: formTranslate?.CIVILITE?.MALE || "Monsieur",
      icon: (active) => <FaceMale active={active} />,
    },
    {
      value: GENDER.FEMALE,
      label: formTranslate?.CIVILITE?.FEMALE || "Madame",
      icon: (active) => <FaceFemale active={active} />,
    },
  ];

  const renderTeletransmission = (namePath) => {
    const teletransmissionEnabled = get(values, `${namePath}`, true);
    return (
      <Input
        type="checkbox"
        variant="round"
        label={
          formTranslate?.OPT_OUT_TELETRANSMISSION ||
          "Je ne souhaite pas de télétransmission automatique avec le régime obligatoire (CPAM, MSA…)."
        }
        checked={!teletransmissionEnabled}
        onChange={(checked) => setFieldValue(namePath, !checked)}
      />
    );
  };

  return (
    <Layout>
      <Header
        title={headerTranslate?.TITLE}
        highlight={headerTranslate?.HIGHLIGHT}
        subtitle={headerTranslate?.SUBTITLE}
      />
      <div className="acoris-form acoris-related-persons">
        <div className="acoris-related-persons__card">
          {!isEmpty(spouse) && (
            <>
              <div className="acoris-related-persons__section">
                <h3 className="acoris-related-persons__section-title">
                  {sectionTranslate?.SPOUSE_PREFIX}
                  <span className="acoris-related-persons__section-title-accent">
                    {sectionTranslate?.SPOUSE_ACCENT}
                  </span>
                </h3>
                <div className="acoris-related-persons__fields">
                  <div className="acoris-form__civilite acoris-related-persons__civilite">
                    <Input
                      label={formTranslate?.CIVILITE?.LABEL || "Civilité"}
                      type="radio"
                      variant="pill"
                      values={civiliteOptions}
                      {...bindInputProps({
                        name: "spouse.identity.user.gender",
                        values,
                        ...formProps,
                      })}
                      onChange={(value) =>
                        setFieldValue("spouse.identity.user.gender", value)
                      }
                    />
                  </div>
                  <div className="acoris-form__row">
                    <Input
                      placeholder={
                        formTranslate?.LASTNAME?.PLACEHOLDER || "Dubois"
                      }
                      label={formTranslate?.LASTNAME?.LABEL || "Nom"}
                      {...bindWebTextFieldInputProps({
                        name: "spouse.identity.user.lastname",
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      placeholder={
                        formTranslate?.FIRSTNAME?.PLACEHOLDER || "John"
                      }
                      label={formTranslate?.FIRSTNAME?.LABEL || "Prénom"}
                      {...bindWebTextFieldInputProps({
                        name: "spouse.identity.user.firstname",
                        values,
                        ...formProps,
                      })}
                    />
                  </div>
                  <Input
                    placeholder={
                      formTranslate?.SOCIAL_SECURITY?.PLACEHOLDER ||
                      "1 85 05 78 006 084 36"
                    }
                    label={
                      formTranslate?.SOCIAL_SECURITY?.LABEL ||
                      "N° de Sécurité sociale"
                    }
                    trim={true}
                    {...bindInputProps({
                      name: "spouse.identity.socialSecurityNumber",
                      values,
                      ...formProps,
                    })}
                    onChange={({ target: { value } }) => {
                      setFieldValue(
                        "spouse.identity.socialSecurityNumber",
                        value.replace(/\s+/g, ""),
                      );
                    }}
                  />
                  {renderTeletransmission(
                    "spouse.identity.fields.teletransmission",
                  )}
                </div>
              </div>
              {(values.children || []).length > 0 && (
                <div className="acoris-related-persons__divider" />
              )}
            </>
          )}
          {(values.children || []).map((_, i) => {
            const authority = get(values, `children.${i}.authority`);
            const childRow = get(values, `children.${i}`);
            const nirInheritanceFrom = getNirInheritanceFromStored(childRow);
            const spouseNir = get(
              values,
              "spouse.identity.socialSecurityNumber",
            );
            const showPending = isPendingParentNir({
              authority,
              nirInheritanceFrom,
              adherentNir: adherentSocialSecurityNumber,
              spouseNir,
              hasSpouse,
            });
            const ssnReadOnly = !childNirIsManualEntry(
              authority,
              hasSpouse,
              nirInheritanceFrom,
            );
            const regimeMismatchMessage =
              get(
                formProps.errors,
                `children.${i}.identity.socialRegime`,
                "",
              ) || get(formProps.errors, `children.${i}.authority`, "");
            const showRegimeMismatch =
              Boolean(regimeMismatchMessage) &&
              authority &&
              authority !== AUTHORITY_TYPE.SELF;

            return (
              <div
                key={`child_${i}`}
                className="acoris-related-persons__section"
              >
                {i > 0 && <div className="acoris-related-persons__divider" />}
                <h3 className="acoris-related-persons__section-title">
                  {sectionTranslate?.CHILD_PREFIX}
                  <span className="acoris-related-persons__section-title-accent">
                    {t("RELATED_PERSONS.SECTION.CHILD_ACCENT", {
                      ordinal: NUMBERS_MAPPING[i + 1],
                    })}
                  </span>
                </h3>
                <div className="acoris-related-persons__fields">
                  <div className="acoris-form__civilite acoris-related-persons__civilite">
                    <Input
                      label={formTranslate?.CIVILITE?.LABEL || "Civilité"}
                      type="radio"
                      variant="pill"
                      values={civiliteOptions}
                      {...bindInputProps({
                        name: `children.${i}.identity.user.gender`,
                        values,
                        ...formProps,
                      })}
                      onChange={(value) =>
                        setFieldValue(
                          `children.${i}.identity.user.gender`,
                          value,
                        )
                      }
                    />
                  </div>
                  <div className="acoris-form__row">
                    <Input
                      placeholder={
                        formTranslate?.LASTNAME?.PLACEHOLDER || "Dubois"
                      }
                      label={formTranslate?.LASTNAME?.LABEL || "Nom"}
                      {...bindWebTextFieldInputProps({
                        name: `children.${i}.identity.user.lastname`,
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      placeholder={
                        formTranslate?.FIRSTNAME?.PLACEHOLDER || "John"
                      }
                      label={formTranslate?.FIRSTNAME?.LABEL || "Prénom"}
                      {...bindWebTextFieldInputProps({
                        name: `children.${i}.identity.user.firstname`,
                        values,
                        ...formProps,
                      })}
                    />
                  </div>
                  <Input
                    placeholder={
                      formTranslate?.SOCIAL_SECURITY?.PLACEHOLDER ||
                      "1 85 05 78 006 084 36"
                    }
                    label={
                      formTranslate?.SOCIAL_SECURITY?.LABEL ||
                      "N° de Sécurité sociale"
                    }
                    trim={true}
                    disabled={ssnReadOnly}
                    {...bindInputProps({
                      name: `children.${i}.identity.socialSecurityNumber`,
                      values,
                      ...formProps,
                    })}
                    onChange={({ target: { value } }) => {
                      setFieldValue(
                        `children.${i}.identity.socialSecurityNumber`,
                        value.replace(/\s+/g, ""),
                      );
                    }}
                  />
                  {renderTeletransmission(
                    `children.${i}.identity.fields.teletransmission`,
                  )}
                  {/* <Input
                    row
                    size="full"
                    label={t("RELATED_PERSONS.AUTHORITY.LABEL")}
                    type="radio"
                    values={[
                      {
                        value: AUTHORITY_TYPE.FATHER,
                        label: t("RELATED_PERSONS.AUTHORITY.FATHER"),
                      },
                      {
                        value: AUTHORITY_TYPE.MOTHER,
                        label: t("RELATED_PERSONS.AUTHORITY.MOTHER"),
                      },
                      {
                        value: AUTHORITY_TYPE.SELF,
                        label: t("RELATED_PERSONS.AUTHORITY.SELF"),
                      },
                    ]}
                    {...bindInputProps({
                      name: `children.${i}.authority`,
                      values,
                      ...formProps,
                    })}
                    onChange={(value) => {
                      setFieldValue(`children.${i}.authority`, value);
                      const baseFields =
                        get(values, `children.${i}.identity.fields`, {}) || {};
                      if (value !== AUTHORITY_TYPE.BOTH) {
                        const next = { ...baseFields };
                        delete next.nirInheritanceFrom;
                        setFieldValue(`children.${i}.identity.fields`, next);
                      } else {
                        setFieldValue(`children.${i}.identity.fields`, {
                          ...baseFields,
                          nirInheritanceFrom: NIR_INHERITANCE_FROM.ADHERENT,
                        });
                      }
                      if (
                        value === AUTHORITY_TYPE.SELF ||
                        childRequiresOffContractSpouseNir(value, hasSpouse)
                      ) {
                        setFieldValue(
                          `children.${i}.identity.socialSecurityNumber`,
                          "",
                        );
                      }
                    }}
                  /> */}
                  {/* {authority === AUTHORITY_TYPE.BOTH && (
                    <Select
                      options={[
                        {
                          value: NIR_INHERITANCE_FROM.ADHERENT,
                          label: t(
                            "RELATED_PERSONS.NIR_INHERITANCE_FROM.ADHERENT",
                          ),
                        },
                        {
                          value: NIR_INHERITANCE_FROM.SPOUSE,
                          label: t(
                            "RELATED_PERSONS.NIR_INHERITANCE_FROM.SPOUSE",
                          ),
                        },
                      ]}
                      placeholder={t(
                        "RELATED_PERSONS.NIR_INHERITANCE_FROM.LABEL",
                      )}
                      label={t("RELATED_PERSONS.NIR_INHERITANCE_FROM.LABEL")}
                      value={nirInheritanceFrom}
                      onChange={(value) => {
                        const baseFields =
                          get(values, `children.${i}.identity.fields`, {}) ||
                          {};
                        setFieldValue(`children.${i}.identity.fields`, {
                          ...baseFields,
                          nirInheritanceFrom: value,
                        });
                        if (
                          childRequiresOffContractSpouseNir(
                            AUTHORITY_TYPE.BOTH,
                            hasSpouse,
                            value,
                          )
                        ) {
                          setFieldValue(
                            `children.${i}.identity.socialSecurityNumber`,
                            "",
                          );
                        }
                      }}
                      errors={get(
                        formProps.errors,
                        `children.${i}.identity.fields.nirInheritanceFrom`,
                        "",
                      )}
                      touched={get(
                        formProps.touched,
                        `children.${i}.identity.fields.nirInheritanceFrom`,
                        false,
                      )}
                    />
                  )}
                  {showPending && (
                    <p
                      className="acoris-related-persons__alert-info"
                      role="status"
                    >
                      {t("RELATED_PERSONS.PARENT_NIR_PENDING", {
                        parent: pendingParentLabel(
                          authority,
                          nirInheritanceFrom,
                        ),
                      })}
                    </p>
                  )}
                  {showRegimeMismatch && (
                    <p
                      className="acoris-related-persons__alert-error"
                      role="alert"
                    >
                      {regimeMismatchMessage}
                    </p>
                  )} */}
                </div>
              </div>
            );
          })}
        </div>
        <ActionButtons
          disabled={!isEmpty(formProps.errors)}
          onNext={submitForm}
          onBack={onBack}
          nextText={t("RELATED_PERSONS.BUTTON.CONTINUE")}
          backText={t("RELATED_PERSONS.BUTTON.BACK")}
        />
      </div>
    </Layout>
  );
}

export default View;
